import { useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { CustomerForm } from './CustomerForm';
import { RecordList } from './RecordList';
import {
  useCustomer,
  useDeactivateCustomer,
  useReactivateCustomer,
  useUpdateCustomer
} from './hooks';
import { toCustomerPayload, type CustomerFormValues } from './customerSchema';
import { usePageTitle } from '../../hooks/usePageTitle';

export function CustomerDetailPage() {
  usePageTitle('Client');
  const { id = '' } = useParams();
  const { data: customer, isLoading } = useCustomer(id);
  const update = useUpdateCustomer(id);
  const deactivate = useDeactivateCustomer();
  const reactivate = useReactivateCustomer();

  const [editing, setEditing] = useState(false);

  if (isLoading) return <div className="max-w-4xl mx-auto px-4 py-8 text-slate-500">Loading…</div>;
  if (!customer)
    return (
      <div className="max-w-4xl mx-auto px-4 py-8 text-slate-500">
        Client not found.{' '}
        <Link to="/customers" className="text-sky-600">
          Back to list
        </Link>
      </div>
    );

  const onSave = async (values: CustomerFormValues) => {
    await update.mutateAsync(toCustomerPayload(values));
    setEditing(false);
  };

  return (
    <div className="max-w-4xl mx-auto px-4 py-8 space-y-6">
      <div className="flex items-start justify-between gap-4">
        <div>
          <Link to="/customers" className="text-sm text-slate-500 hover:text-sky-600">
            ← Clients
          </Link>
          <h1 className="text-2xl font-semibold text-slate-800 mt-2 flex items-center gap-3">
            {customer.displayName}
            {!customer.isActive && (
              <span className="text-xs bg-slate-100 text-slate-600 border border-slate-200 px-2 py-0.5 rounded">
                Inactive
              </span>
            )}
          </h1>
        </div>
        {!editing && (
          <div className="flex gap-2 text-sm">
            <button
              type="button"
              onClick={() => setEditing(true)}
              className="border border-slate-300 text-slate-700 px-3 py-2 rounded hover:bg-slate-50"
            >
              Edit
            </button>
            <Link
              to={`/reports?tab=customer&customerId=${customer.id}`}
              className="border border-slate-300 text-slate-700 px-3 py-2 rounded hover:bg-slate-50"
            >
              Report
            </Link>
            <Link
              to={`/estimates?customerId=${customer.id}`}
              className="border border-slate-300 text-slate-700 px-3 py-2 rounded hover:bg-slate-50"
            >
              Estimates
            </Link>
            {customer.isActive ? (
              <button
                type="button"
                onClick={() => {
                  if (confirm(`Deactivate ${customer.displayName}?`))
                    deactivate.mutate(customer.id);
                }}
                className="border border-slate-300 text-slate-700 px-3 py-2 rounded hover:bg-slate-50"
              >
                Deactivate
              </button>
            ) : (
              <button
                type="button"
                onClick={() => reactivate.mutate(customer.id)}
                className="border border-slate-300 text-slate-700 px-3 py-2 rounded hover:bg-slate-50"
              >
                Reactivate
              </button>
            )}
          </div>
        )}
      </div>

      {editing ? (
        <div className="space-y-4">
          <h2 className="font-display text-lg font-semibold text-ink">Edit client</h2>
          <CustomerForm
            submitLabel="Save"
            defaultValues={{
              displayName: customer.displayName,
              email: customer.email ?? '',
              phone: customer.phone ?? '',
              preferredDeliveryChannel: customer.preferredDeliveryChannel,
              addressLine1: customer.addressLine1 ?? '',
              addressLine2: customer.addressLine2 ?? '',
              city: customer.city ?? '',
              region: customer.region ?? '',
              postalCode: customer.postalCode ?? '',
              country: customer.country ?? '',
              notes: customer.notes ?? ''
            }}
            onSubmit={onSave}
            onCancel={() => setEditing(false)}
            submitting={update.isPending}
          />
        </div>
      ) : (
        <section className="bg-white border border-slate-200 rounded-xl p-6">
          <CustomerSummary customer={customer} />
        </section>
      )}

      <section className="bg-white border border-slate-200 rounded-xl p-6">
        <RecordList customerId={customer.id} />
      </section>
    </div>
  );
}

function CustomerSummary({ customer }: { customer: ReturnType<typeof useCustomer>['data'] & {} }) {
  return (
    <dl className="grid sm:grid-cols-2 gap-x-8 gap-y-3 text-sm">
      <DlRow label="Email" value={customer.email} />
      <DlRow label="Phone" value={customer.phone} />
      <DlRow
        label="Preferred delivery"
        value={
          customer.preferredDeliveryChannel === 'Sms' ? (
            <span className="inline-flex items-center gap-2">
              SMS
              <span className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded px-2 py-0.5">
                coming soon — customer still gets email
              </span>
            </span>
          ) : (
            'Email'
          )
        }
      />
      <DlRow
        label="Address"
        value={[
          customer.addressLine1,
          customer.addressLine2,
          [customer.city, customer.region].filter(Boolean).join(', '),
          customer.postalCode,
          customer.country
        ]
          .filter(Boolean)
          .join(' • ')}
      />
      {customer.notes && <DlRow label="Notes" value={customer.notes} full />}
    </dl>
  );
}

function DlRow({
  label,
  value,
  full
}: {
  label: string;
  value: React.ReactNode;
  full?: boolean;
}) {
  return (
    <div className={full ? 'sm:col-span-2' : ''}>
      <dt className="text-xs uppercase tracking-wide text-slate-500">{label}</dt>
      <dd className="text-slate-800 mt-0.5 break-words">{value || <em className="text-slate-400">—</em>}</dd>
    </div>
  );
}
